// DashboardPage.js - OPRAVENÁ VERZE podle funkčního kódu
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FolderPlus, 
  Filter, 
  X, 
  Search,
  Camera,
  Clock,
  Edit,
  Upload,
  LayoutGrid,
  LayoutList,
  Eye,
  Plus
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { projects, users } from '../services/api';
import Header from '../components/layout/Header';
import ProjectModal from '../components/projects/ProjectModal';
import WorkLogModal from '../components/projects/WorkLogModal';
import FileUpload from '../components/projects/FileUpload';
import ProjectCard from '../components/projects/ProjectCard';
import StatusFilter from '../components/projects/StatusFilter';
import ViewSwitch from '../utils/ViewSwitch';
import { useTranslation } from 'react-i18next';
import ProjectDetailsModal from '../components/projects/ProjectDetailsModal';

const DashboardPage = () => {
  const { t } = useTranslation();
  const [projectsList, setProjectsList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isWorkLogModalOpen, setIsWorkLogModalOpen] = useState(false);
  const [isFileUploadOpen, setIsFileUploadOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [selectedWorkLog, setSelectedWorkLog] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(['Active']);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCardView, setIsCardView] = useState(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [projectForDetails, setProjectForDetails] = useState(null);
  const { user, isAdmin, isOfficeAdmin } = useAuth();

  // ✅ useRef pro řešení closure problému
  const currentProjectIdRef = useRef(null);

  // ✅ Helper funkce pro ID access
  const getProjectId = (project) => {
    if (!project) {
      console.warn('⚠️ getProjectId called with null/undefined project');
      return null;
    }
    const id = project.id || project._id;
    return id;
  };
  
  const getUserId = (user) => user.id || user._id;

  useEffect(() => {
    fetchProjects();
    if (isAdmin() || isOfficeAdmin()) {
      fetchTechnicians();
    }
  }, []);

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await projects.getAll();
      console.log('📊 Fetched projects:', data);
      setProjectsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      setError(t('dashboard.errors.fetchFailed', 'Failed to load projects'));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch technicians for project assignment
  const fetchTechnicians = async () => {
    try {
      const { data } = await users.getAll();
      setTechnicians(data.filter(user => user.role === 'technician'));
    } catch (error) {
      console.error('❌ Error fetching technicians:', error);
    }
  };

  // ✅ KRITICKÁ OPRAVA #1: Selective Project Update
  const updateSingleProject = useCallback(async (projectId, updatedData = null) => {
    console.log('🔄 Selective update for project:', projectId);
    
    try {
      let projectData = updatedData;
      
      if (!projectData) {
        const { data } = await projects.getById(projectId);
        projectData = data;
      }
      
      setProjectsList(prev => 
        prev.map(p => {
          const pId = getProjectId(p);
          if (pId === projectId) {
            console.log('✅ Updated project in list:', projectData.name);
            return { ...p, ...projectData };
          }
          return p;
        })
      );
      
    } catch (error) {
      console.error('❌ Error updating single project:', error);
      await fetchProjects();
    }
  }, []);

  // ✅ KRITICKÁ OPRAVA #2: Refresh project details
  const refreshProjectDetails = useCallback(async (projectId) => {
    console.log('🔄 Refreshing project details for ID:', projectId);
    
    try {
      const { data } = await projects.getById(projectId);
      console.log('✅ Project details refreshed:', data.name, 'Files:', data.files?.length || 0);
      
      // ✅ DEBUG: Log file details
      if (data.files && data.files.length > 0) {
        console.log('📁 Files in refreshed project:', data.files.map(f => ({
          id: f.id || f._id,
          name: f.fileName || f.file_name,
          type: f.fileType || f.file_type
        })));
      }
      
      setProjectsList(prev => 
        prev.map(p => {
          const pId = getProjectId(p);
          if (pId === projectId) {
            return { ...p, ...data };
          }
          return p;
        })
      );
      
      if (projectForDetails && getProjectId(projectForDetails) === projectId) {
        setProjectForDetails(data);
      }
      
    } catch (error) {
      console.error('❌ Error refreshing project details:', error);
    }
  }, [projectForDetails]);

  // Project Modal Handlers
  const handleCreateProject = () => {
    setSelectedProject(null);
    setIsProjectModalOpen(true);
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setIsProjectModalOpen(true);
  };

  const handleSaveProject = async (projectData) => {
    try {
      setError(null);
      if (selectedProject) {
        const projectId = getProjectId(selectedProject);
        const { data } = await projects.update(projectId, projectData);
        updateSingleProject(projectId, data);
      } else {
        const { data } = await projects.create(projectData);
        setProjectsList(prev => [data, ...prev]);
      }
      setIsProjectModalOpen(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('❌ Error saving project:', error);
      setError(t('dashboard.errors.saveFailed', 'Failed to save project'));
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      setError(null);
      await projects.delete(projectId);
      setProjectsList(prev => prev.filter(p => getProjectId(p) !== projectId));
      setIsProjectModalOpen(false);
      setSelectedProject(null);
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      setError(t('dashboard.errors.deleteFailed', 'Failed to delete project'));
    }
  };

  // ✅ OPRAVENÉ File Upload Handlers
  const handleAddFile = (projectId) => {
    console.log('📁 handleAddFile called for project ID:', projectId);
    
    if (!projectId) {
      console.error('❌ Invalid project ID for file upload:', projectId);
      setError('Invalid project selected');
      return;
    }

    setCurrentProjectId(projectId);
    currentProjectIdRef.current = projectId;
    setIsFileUploadOpen(true);
    
    console.log('✅ File upload modal opened for project ID:', projectId);
  };

const handleSaveFile = async (formDataOrFiles) => {
  console.log('💾 handleSaveFile called...');
  
  const projectId = currentProjectIdRef.current;
  console.log('💾 Project ID from ref:', projectId);
  
  if (!projectId) {
    console.error('❌ No project selected for file upload');
    setError('No project selected for file upload');
    return;
  }

  try {
    setError('');
    
    // Handle FormData (modern approach)
    if (formDataOrFiles instanceof FormData) {
      console.log('🆕 Processing FormData upload for project:', projectId);
      
      const result = await projects.addFile(projectId, formDataOrFiles, (progress) => {
        console.log(`📊 Upload progress: ${progress}%`);
      });
      
      console.log('✅ FormData file uploaded successfully:', result.data);
      
    } else if (Array.isArray(formDataOrFiles)) {
      console.log('📙 Processing legacy base64 upload for', formDataOrFiles.length, 'files to project:', projectId);
      
      for (const fileData of formDataOrFiles) {
        if (!fileData.base64) {
          console.warn('⚠️ Skipping file - missing base64 data');
          continue;
        }
        await projects.addFileLegacy(projectId, fileData);
      }
    } else {
      console.error('❌ Unknown file data format:', typeof formDataOrFiles);
      throw new Error('Unknown file data format');
    }
    
    // ✅ KRITICKÁ OPRAVA: Refresh project details, ale NEUZAVÍREJ modal
    console.log('✅ Upload completed, refreshing project details...');
    await refreshProjectDetails(projectId);
    
    // ✅ NERESETTUJ STATE TADY - to udělá FileUpload.js po všech souborech
    // Modal se zavře až když FileUpload dokončí všechny soubory
    
  } catch (error) {
    console.error('❌ Error uploading file:', error);
    setError(error.response?.data?.message || error.message || 'Failed to upload file');
    throw error; // ✅ Throw error zpět do FileUpload.js pro správné error handling
  }
};

  // Work Log Handlers
  const handleAddWorkLog = (projectId) => {
    console.log('📋 handleAddWorkLog called for project ID:', projectId);
    
    if (!projectId) {
      console.error('❌ Invalid project ID for work log:', projectId);
      setError('Invalid project selected');
      return;
    }

    setCurrentProjectId(projectId);
    currentProjectIdRef.current = projectId;
    setSelectedWorkLog(null);
    setIsWorkLogModalOpen(true);
    
    console.log('✅ Work log modal opened for project ID:', projectId);
  };

  const handleEditWorkLog = (workLog, projectId) => {
    console.log('✏️ handleEditWorkLog called for work log:', workLog._id, 'project ID:', projectId);
    
    if (!projectId) {
      console.error('❌ Invalid project ID for work log edit:', projectId);
      setError('Invalid project selected');
      return;
    }

    setCurrentProjectId(projectId);
    currentProjectIdRef.current = projectId;
    setSelectedWorkLog(workLog);
    setIsWorkLogModalOpen(true);
  };

  const handleSaveWorkLog = async (workLogData) => {
    const projectId = currentProjectIdRef.current;
    console.log('💾 Saving work log for project:', projectId);
    
    if (!projectId) {
      setError('No project selected');
      return;
    }

    try {
      setError('');
      
      if (selectedWorkLog) {
        console.log('✏️ Updating existing work log:', selectedWorkLog._id);
        await projects.updateWorkLog(projectId, selectedWorkLog._id, workLogData);
      } else {
        console.log('➕ Creating new work log');
        await projects.addWorkLog(projectId, workLogData);
      }
      
      await refreshProjectDetails(projectId);
      
      setIsWorkLogModalOpen(false);
      setSelectedWorkLog(null);
      setCurrentProjectId(null);
      currentProjectIdRef.current = null;
      
    } catch (error) {
      console.error('❌ Error saving work log:', error);
      setError(error.response?.data?.message || error.message || 'Failed to save work log');
    }
  };

  const handleDeleteWorkLog = async (project, workLogId) => {
    const projectId = getProjectId(project);
    console.log('🗑️ Deleting work log:', workLogId, 'from project:', projectId);
    
    if (!window.confirm(t('workLogs.confirmDelete', 'Are you sure you want to delete this work log?'))) {
      return;
    }

    try {
      if (!projectId) {
        console.error('❌ Project ID missing for work log deletion');
        return;
      }
      
      await projects.deleteWorkLog(projectId, workLogId);
      await refreshProjectDetails(projectId);
      
    } catch (error) {
      console.error('❌ Error deleting work log:', error);
      setError(t('workLogs.errors.deleteWorkLog', 'Failed to delete work log'));
    }
  };

  // Project Details Modal Handlers
  const handleViewProjectDetails = (project) => {
    setProjectForDetails(project);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setProjectForDetails(null);
  };

  // Filter logic
  const filteredProjects = projectsList.filter(project => {
    const searchMatch = project.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(project.status);
    return searchMatch && statusMatch;
  });

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('cs-CZ', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric'
      });
    } catch (error) {
      console.warn('Invalid date:', dateString);
      return 'Invalid Date';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-500"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* ✅ VYLEPŠENÁ HLAVIČKA */}
        <div className="mb-8">
          {/* Title Section */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('dashboard.title', 'Všechny projekty')}
              </h1>
              <p className="text-gray-600 mt-1">
                {user.role === 'technician' 
                  ? t('dashboard.subtitle.technician', 'Vaše přiřazené projekty a úkoly')
                  : t('dashboard.subtitle.admin', 'Spravujte a sledujte všechny projekty')
                }
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {(isAdmin() || isOfficeAdmin()) && (
                <button
                  onClick={handleCreateProject}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('dashboard.newProject', 'Nový projekt')}
                </button>
              )}
              <ViewSwitch isCardView={isCardView} onViewChange={setIsCardView} />
            </div>
          </div>

          {/* Search and Filters Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('dashboard.search', 'Hledat projekty...')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
                  isFilterOpen || selectedStatuses.length !== 1 || selectedStatuses[0] !== 'Active'
                    ? 'bg-blue-50 text-blue-700 border-blue-300'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Filter className="h-4 w-4 mr-2" />
                {isFilterOpen 
                  ? t('dashboard.hideFilters', 'Skrýt filtry') 
                  : t('dashboard.showFilters', 'Zobrazit filtry')}
                {(selectedStatuses.length !== 1 || selectedStatuses[0] !== 'Active') && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {selectedStatuses.length}
                  </span>
                )}
              </button>
            </div>

            {/* ✅ OPRAVENÝ FILTER */}
            {isFilterOpen && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    {t('dashboard.filterByStatus', 'Filtrovat podle stavu')}
                  </h3>
                  <StatusFilter
                    selectedStatuses={selectedStatuses}
                    onChange={setSelectedStatuses}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
            {error}
            <button 
              onClick={() => setError(null)}
              className="ml-4 text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Project Count */}
        {filteredProjects.length > 0 && (
          <div className="mb-4 text-sm text-gray-600">
            {t('dashboard.showingProjects', 'Zobrazeno {{filtered}} z {{total}} projektů', {
              filtered: filteredProjects.length,
              total: projectsList.length
            })}
          </div>
        )}

        {/* Projects Grid/List */}
        {isCardView ? (
          // Card View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => {
              const projectId = getProjectId(project);
              console.log('🎴 Rendering ProjectCard for project:', project.name, 'with ID:', projectId);
              
              return (
                <ProjectCard
                  key={projectId}
                  project={project}
                  onEdit={handleEditProject}
                  onAddWorkLog={handleAddWorkLog}      
                  onEditWorkLog={handleEditWorkLog}    
                  onDeleteWorkLog={handleDeleteWorkLog} 
                  onAddFile={handleAddFile}           
                  userRole={user.role}
                  currentUserId={getUserId(user)}
                  // ✅ KRITICKÁ OPRAVA: Selective update
                  onProjectUpdate={(updatedProject) => {
                    console.log('📨 ProjectCard update received for:', projectId);
                    updateSingleProject(projectId, updatedProject);
                  }}
                />
              );
            })}
          </div>
        ) : (
          // Table View
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('projects.details', 'PROJECT DETAILS')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('projects.status', 'STAV')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('projects.startDate', 'DATUM ZAHÁJENÍ')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions.title', 'AKCE')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProjects.map((project) => {
                    const projectId = getProjectId(project);
                    const contractNumber = project.contractNumber || project.contract_number || '';
                    const startDate = project.startDate || project.start_date || '';
                    const projectName = project.name || t('projects.unnamed', 'Nepojmenovaný projekt');
                    const status = project.status || 'Active';
                    
                    const getStatusColor = (status) => {
                      switch (status?.toLowerCase()) {
                        case 'active':
                        case 'aktivní':
                          return 'bg-green-100 text-green-800';
                        case 'pending':
                          return 'bg-yellow-100 text-yellow-800';
                        case 'completed':
                          return 'bg-blue-100 text-blue-800';
                        case 'onhold':
                          return 'bg-orange-100 text-orange-800';
                        case 'cancelled':
                          return 'bg-red-100 text-red-800';
                        default:
                          return 'bg-gray-100 text-gray-800';
                      }
                    };

                    return (
                      <tr key={projectId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-gray-900">
                              {projectName}
                            </div>
                            <div className="text-sm text-gray-500">
                              #{contractNumber}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                            {status === 'Active' ? 'Aktivní' : status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(startDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end items-center space-x-2">
                            <button
                              onClick={() => handleViewProjectDetails(project)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded"
                              title={t('projects.viewDetails', 'Zobrazit detaily')}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleAddFile(project._id)}
                              className="text-green-600 hover:text-green-800 p-1 rounded"
                              title={t('files.uploadFiles', 'Nahrát soubory')}
                            >
                              <Upload className="h-4 w-4" />
                            </button>
                            {(isAdmin() || isOfficeAdmin()) && (
                              <button
                                onClick={() => handleEditProject(project)}
                                className="text-gray-600 hover:text-gray-800 p-1 rounded"
                                title={t('common.actions.edit', 'Upravit')}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <FolderPlus className="h-12 w-12" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {searchTerm || selectedStatuses.length > 0 
                ? t('dashboard.noProjectsFiltered', 'Žádné projekty nevyhovují zadaným kritériím')
                : t('dashboard.noProjects', 'Zatím žádné projekty')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedStatuses.length > 0 
                ? t('dashboard.adjustFilters', 'Zkuste upravit vyhledávání nebo filtry')
                : t('dashboard.getStarted', 'Začněte vytvořením nového projektu')}
            </p>
            {(isAdmin() || isOfficeAdmin()) && !searchTerm && selectedStatuses.length === 0 && (
              <div className="mt-6">
                <button
                  onClick={handleCreateProject}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('dashboard.createFirstProject', 'Vytvořit první projekt')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        <ProjectModal
          isOpen={isProjectModalOpen}
          onClose={() => {
            setIsProjectModalOpen(false);
            setSelectedProject(null);
          }}
          onSave={handleSaveProject}
          onDelete={handleDeleteProject}
          project={selectedProject}
          technicians={technicians}
        />

        <WorkLogModal
          isOpen={isWorkLogModalOpen}
          onClose={() => {
            setIsWorkLogModalOpen(false);
            setSelectedWorkLog(null);
            setCurrentProjectId(null);
            currentProjectIdRef.current = null;
          }}
          onSave={handleSaveWorkLog}
          workLog={selectedWorkLog}
          projectId={currentProjectId}
          technicians={technicians}
        />

        <FileUpload
          isOpen={isFileUploadOpen}
          onClose={() => {
            console.log('🔒 Closing FileUpload modal and resetting state');
            setIsFileUploadOpen(false);
            setCurrentProjectId(null);
            currentProjectIdRef.current = null;
          }}
          onSave={handleSaveFile}
        />

        <ProjectDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          project={projectForDetails}
          onEdit={handleEditProject}
          onAddWorkLog={handleAddWorkLog}
          onEditWorkLog={handleEditWorkLog}
          onDeleteWorkLog={handleDeleteWorkLog}
          onAddFile={handleAddFile}
          userRole={user.role}
          currentUserId={getUserId(user)}
          // ✅ KRITICKÁ OPRAVA: Selective update i pro modal
          onProjectUpdate={(updatedProject) => {
            const projectId = getProjectId(updatedProject);
            console.log('📨 ProjectDetailsModal update received for:', projectId);
            updateSingleProject(projectId, updatedProject);
          }}
        />
      </main>
    </div>
  );
};

export default DashboardPage;